import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  TournamentCreateModel,
  WeightCategoryInput,
  AgeCategoryInput,
  TournamentClient,
  Gender,
} from '../../utils/apiClient';
import axios from 'axios';
import ApiUrl from '../../utils/config';
import { useAuth } from '../../components/Auth/AuthContext';
import AppContext from '../../AppContext';
import { handleError } from '../../utils';
import { DateLocal, DateType } from '../../common/components/DateLocal/DateLocal';
import { AgeCategoriesEditor, WeightCategoriesEditor } from '../CategoryLimitsEditor/CategoryLimitsEditor';
import {
  AgeCategoryDraft,
  WeightCategoryDraft,
  toAgeDraftsFromTournament,
  toWeightDraftsFromTournament,
  validateAgeDrafts,
  validateWeightDrafts,
  validateGenderedTournamentDrafts,
} from '../../utils/categoryResolver';
import {
  buildGenderedCategoryInputs,
  GenderedCategoriesSection,
} from '../CategoryLimitsEditor/GenderedCategoriesSection';
import { isGenderedTournament } from '../../utils/genderCategoryUtils';

const UpdateTournament: React.FC = () => {
  const appContext = React.useContext(AppContext);
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isLegacyCategories, setIsLegacyCategories] = useState(true);
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const tournamentClient = new TournamentClient(ApiUrl, axios);

  const [tournament, setTournament] = useState<TournamentCreateModel>(new TournamentCreateModel({
    name: '',
    location: '',
    startDate: new Date(),
    endDate: new Date(),
    registrationEndDate: new Date(),
    durationInMinutes: 3,
    weightCategories: [],
    ageCategories: [],
  }));

  // Обычно турнир идёт один день и дата окончания равна дате начала. Поле окончания показываем
  // только у старых многодневных турниров, чтобы при сохранении не сократить их до одного дня.
  const [isMultiDay, setIsMultiDay] = useState(false);

  const [weightDrafts, setWeightDrafts] = useState<WeightCategoryDraft[]>([]);
  const [ageDrafts, setAgeDrafts] = useState<AgeCategoryDraft[]>([]);
  const [boysWeight, setBoysWeight] = useState<WeightCategoryDraft[]>([]);
  const [boysAge, setBoysAge] = useState<AgeCategoryDraft[]>([]);
  const [girlsWeight, setGirlsWeight] = useState<WeightCategoryDraft[]>([]);
  const [girlsAge, setGirlsAge] = useState<AgeCategoryDraft[]>([]);

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/tournaments');
    }
  }, [isAdmin, navigate]);

  const fetchTournament = async () => {
    try {
      const response = await tournamentClient.getTournament(parseInt(id!));
      setIsMultiDay(new Date(response.endDate).toDateString() !== new Date(response.startDate).toDateString());

      setTournament(new TournamentCreateModel({
        name: response.name,
        location: response.location,
        startDate: new Date(response.startDate),
        endDate: new Date(response.endDate),
        registrationEndDate: new Date(response.registrationEndDate),
        durationInMinutes: response.durationInMinutes,
        chiefJudgeName: response.chiefJudgeName,
        chiefSecretaryName: response.chiefSecretaryName,
        weightCategories: [],
        ageCategories: [],
      }));

      const weights = response.weightCategories ?? [];
      const ages = response.ageCategories ?? [];
      const gendered = isGenderedTournament(weights, ages);
      setIsLegacyCategories(!gendered);

      if (gendered) {
        setBoysWeight(toWeightDraftsFromTournament(weights, Gender.Male));
        setBoysAge(toAgeDraftsFromTournament(ages, Gender.Male));
        setGirlsWeight(toWeightDraftsFromTournament(weights, Gender.Female));
        setGirlsAge(toAgeDraftsFromTournament(ages, Gender.Female));
      } else {
        setWeightDrafts(toWeightDraftsFromTournament(weights));
        setAgeDrafts(toAgeDraftsFromTournament(ages));
      }
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      if (id) await fetchTournament();
      setLoading(false);
    };
    fetchData();
  }, [id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setTournament(new TournamentCreateModel({ ...tournament, [name]: value }));
  };

  const setDate = (field: 'startDate' | 'endDate' | 'registrationEndDate') => (date: Date | undefined) => {
    if (!date) return;
    setTournament(new TournamentCreateModel({
      ...tournament,
      [field]: date,
      // Однодневный турнир: дата окончания следует за датой турнира
      ...(field === 'startDate' && !isMultiDay ? { endDate: date } : {}),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (new Date(tournament.registrationEndDate).setHours(0, 0, 0, 0) > new Date(tournament.startDate).setHours(0, 0, 0, 0)) {
      setError('Регистрация должна заканчиваться не позже даты турнира');
      return;
    }
    if (isMultiDay && new Date(tournament.endDate).setHours(0, 0, 0, 0) < new Date(tournament.startDate).setHours(0, 0, 0, 0)) {
      setError('Дата окончания не может быть раньше даты начала');
      return;
    }

    if (isLegacyCategories) {
      const weightError = validateWeightDrafts(weightDrafts);
      if (weightError) {
        setError(weightError);
        return;
      }
      const ageError = validateAgeDrafts(ageDrafts);
      if (ageError) {
        setError(ageError);
        return;
      }
    } else {
      const categoryError = validateGenderedTournamentDrafts(boysWeight, boysAge, girlsWeight, girlsAge);
      if (categoryError) {
        setError(categoryError);
        return;
      }
    }

    const merged = isLegacyCategories
      ? { weightCategories: weightDrafts, ageCategories: ageDrafts }
      : buildGenderedCategoryInputs(boysWeight, boysAge, girlsWeight, girlsAge);

    const payload = new TournamentCreateModel({
      ...tournament,
      endDate: isMultiDay ? tournament.endDate : tournament.startDate,
      weightCategories: merged.weightCategories.map(w => new WeightCategoryInput({
        sortOrder: w.sortOrder,
        maxWeight: w.isOpenEnded ? undefined : w.maxWeight,
        isOpenEnded: w.isOpenEnded,
        gender: w.gender,
      })),
      ageCategories: merged.ageCategories.map(a => new AgeCategoryInput({
        sortOrder: a.sortOrder,
        maxAge: a.isOpenEnded ? undefined : a.maxAge,
        isOpenEnded: a.isOpenEnded,
        name: a.name?.trim() || '',
        gender: a.gender,
      })),
    });

    try {
      await tournamentClient.updateTournament(parseInt(id!), payload);
      setSuccess(true);
      setTimeout(() => navigate(`/tournaments/${id}`), 1500);
    } catch (ex: any) {
      const serverMessage = typeof ex?.response === 'string' ? ex.response : undefined;
      if (serverMessage) setError(serverMessage);
      handleError(appContext!.setNotificationData, ex, serverMessage);
    }
  };

  if (loading) {
    return <div>Загрузка данных турнира...</div>;
  }

  return (
    <div className="edit-tournament">
      <h2>Редактирование турнира</h2>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">Турнир успешно обновлен</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Название:</label>
          <input type="text" name="name" value={tournament.name} onChange={handleInputChange} required />
        </div>

        <div className="form-group">
          <label>Место проведения:</label>
          <input type="text" name="location" value={tournament.location} onChange={handleInputChange} required />
        </div>

        <div className="form-group">
          <label>{isMultiDay ? 'Дата начала:' : 'Дата турнира:'}</label>
          <DateLocal type={DateType.date} value={new Date(tournament.startDate)} onChange={setDate('startDate')} width={200} isClearable={false} />
        </div>

        {isMultiDay && (
          <div className="form-group">
            <label>Дата окончания:</label>
            <DateLocal type={DateType.date} value={new Date(tournament.endDate)} onChange={setDate('endDate')} width={200} isClearable={false} />
          </div>
        )}

        <div className="form-group">
          <label>Дата окончания регистрации:</label>
          <DateLocal type={DateType.date} value={new Date(tournament.registrationEndDate)} onChange={setDate('registrationEndDate')} width={200} isClearable={false} />
        </div>

        <div className="form-group">
          <label>Продолжительность поединка (минуты):</label>
          <input type="number" name="durationInMinutes" value={tournament.durationInMinutes} onChange={handleInputChange} required min="1" />
        </div>

        <div className="form-group">
          <label>Главный судья (ФИО для протоколов):</label>
          <input
            type="text"
            name="chiefJudgeName"
            value={tournament.chiefJudgeName ?? ''}
            onChange={handleInputChange}
            placeholder="Иванов Иван Иванович"
            maxLength={150}
          />
        </div>

        <div className="form-group">
          <label>Главный секретарь (ФИО для протоколов):</label>
          <input
            type="text"
            name="chiefSecretaryName"
            value={tournament.chiefSecretaryName ?? ''}
            onChange={handleInputChange}
            placeholder="Петров Пётр Петрович"
            maxLength={150}
          />
        </div>

        <p className="form-hint">
          Название, даты, место и судей можно менять в любой момент.
          Пустые весовые и возрастные категории (без участников, сеток и заполненных протоколов)
          можно удалить даже после начала турнира.
        </p>

        {isLegacyCategories ? (
          <>
            <div className="form-group categories">
              <label>Возрастные категории (общие):</label>
              <AgeCategoriesEditor categories={ageDrafts} onChange={setAgeDrafts} />
            </div>
            <div className="form-group categories">
              <label>Весовые категории (общие):</label>
              <WeightCategoriesEditor categories={weightDrafts} onChange={setWeightDrafts} />
            </div>
          </>
        ) : (
          <div className="form-group categories">
            <label>Категории по полу</label>
            <GenderedCategoriesSection
              gender={Gender.Male}
              title="Мальчики"
              weightDrafts={boysWeight}
              ageDrafts={boysAge}
              onWeightChange={setBoysWeight}
              onAgeChange={setBoysAge}
            />
            <GenderedCategoriesSection
              gender={Gender.Female}
              title="Девочки"
              weightDrafts={girlsWeight}
              ageDrafts={girlsAge}
              onWeightChange={setGirlsWeight}
              onAgeChange={setGirlsAge}
            />
          </div>
        )}

        <div className="form-actions">
          <button type="submit" className="btn">Сохранить изменения</button>
          <button type="button" className="btn btn--back" onClick={() => navigate(`/tournaments/${id}`)}>Отмена</button>
        </div>
      </form>
    </div>
  );
};

export default UpdateTournament;
